import { Format, NumberFormatter } from '@zervant/reactcomponents'
import classNames from 'classnames'
import { Segment } from 'client/Global/Segment'
import { SegmentEvents } from 'client/Global/SegmentEvents'
import { l, tsxl } from 'client/Other/Translation'
import { filter, omit } from 'lodash'
import * as React from 'react'
import { animated, useSpring } from 'react-spring'
import { useMeasure } from 'react-use'

import { isInvoiceTypeWithMandatoryVatColumn } from '../../../Global/InvoiceTypes'
import useDelayed from '../../../hooks/useDelayed'
import { DocumentTypes } from '../../../Interfaces/Draft/DraftInterface'
import { FieldCreator } from '../../common/FieldCreator'
import { NumberInput } from '../../common/NumberInput'
import { ColumnsSettings } from '../ColumnsSettings/ColumnsSettings'
import { InvoiceEditorPropsInterface } from '../InvoiceEditor'
import ReferenceFieldsSettings from '../ReferenceFieldsSettings'
import { getDeductionFromInvoiceType } from '../utils/getDeductionFromInvoiceType'
import { getInvoiceTypesMap } from '../utils/getInvoiceTypesMap'
import './InvoiceSettings.scss'

const getExchangeRateF = (props) => {
  const {
    draft: { fields },
  } = props
  // The real rate will be updated automatically
  const exchangeRateNum = fields.exchangeRate ? fields.exchangeRate.value : 1
  const f = Format.fromLocale(props.token.locale)
  const stringValue = '' + exchangeRateNum
  const a = stringValue.split(f.numbers.decimalSeparator)
  f.numbers.decimalAccuracy =
    a.length > 1 && a[1].length > f.numbers.decimalAccuracy ? a[1].length : f.numbers.decimalAccuracy
  return new NumberFormatter(f)
}

const getExchangeRate = (props): number => {
  const {
    draft: { fields },
  } = props
  return fields.exchangeRate ? fields.exchangeRate.value : 1
}

interface InvoiceSettingsProps extends InvoiceEditorPropsInterface {
  handleCurrencyChange: (selectedCurrency: string) => void
  handleExchangeRateChange: (valueChange: number) => void
}

const InvoiceSettings: React.FC<InvoiceSettingsProps> = (props) => {
  const {
    company,
    config,
    draft,
    selectMaps,
    internal,
    automation,
    settings,
    changeFields,
    changeStyles,
    navigateToInvoicingSettings,
    handleCurrencyChange,
    handleExchangeRateChange,
  } = props
  const [collapsed, setCollapsed] = React.useState(true)
  const fields = draft?.fields
  const allInvoiceTypes = config.invoiceTypes

  const { visibleColumns, columnsNamesMap } = React.useMemo(() => {
    const filteredVatInvoiceTypes = Object.values(allInvoiceTypes).filter(isInvoiceTypeWithMandatoryVatColumn)
    const vatInvoiceTypeIds = filteredVatInvoiceTypes.map((type) => type?.id)
    const shouldPreventHidingVatId = vatInvoiceTypeIds.includes(fields?.type?.value)

    const visibleColumns = shouldPreventHidingVatId
      ? filter(internal.visibleColumns, (colName) => colName !== 'vatPct')
      : internal.visibleColumns
    const columnsNamesMap = shouldPreventHidingVatId ? omit(selectMaps.columnNames, 'vatPct') : selectMaps.columnNames

    return { visibleColumns, columnsNamesMap }
  }, [allInvoiceTypes, internal.visibleColumns, selectMaps.columnNames])

  const updateTypeField = React.useCallback(
    (value: number) => {
      changeFields?.({
        ...fields,
        type: { ...fields.type, value: Number(value) },
        deduction: { ...fields.deduction, ...getDeductionFromInvoiceType(Number(value)) },
        discountLocation: {
          name: 'discountLocation',
          value: [10, 11].includes(Number(value)) ? 'perLine' : fields.discountLocation.value,
        },
      })
    },
    [fields, changeFields],
  )

  const invoiceTypesMap = React.useMemo(
    () => getInvoiceTypesMap(company, config, draft?.receiver, selectMaps?.invoiceTypes),
    [selectMaps?.invoiceTypes, company, config, draft?.receiver],
  )

  const selectedCurrency = fields.currency.value
  const shouldRenderRate = selectedCurrency !== internal.defaultCurrency

  const [ref, { height }] = useMeasure()
  const animatedStyle = useSpring({ height: !collapsed ? height : 0 })
  /**
   * when invoiceSettings is collapsed its overflow needs to be hidden
   * when it is open, overflow needs to be visible for the dropdowns
   * we need delayedCollapse because we want to set overflow after the animation is over
   */
  const delayedCollapsed = useDelayed(collapsed, { only: false, timeout: 500 })

  return (
    <>
      <div className={classNames('InvoiceSettings__header', { collapsed })} onClick={() => setCollapsed(!collapsed)}>
        {tsxl('editor.InvoiceSettings')}
        <span className="InvoiceSettings__collapseIcon"></span>
      </div>
      {
        <animated.div
          className={classNames('InvoiceSettings__container', { collapsed: delayedCollapsed })}
          style={animatedStyle}
        >
          <div ref={ref}>
            <FieldCreator
              name="type"
              className="subFields insetTopShadow"
              model={internal.model}
              schema={internal.schema}
              translator={tsxl}
              props={{
                value: fields.type ? fields.type.value : '',
                searchable: true,
                searchPlaceholder: l('search.Default'),
                map: invoiceTypesMap,
                automation: automation + '-invoice-type',
                sortFn: (a, b) => {
                  const [ak, bk] = [a.key, b.key].map((c) => Number(c))
                  if (ak === 127) {
                    return bk < 3 ? 1 : -1
                  }
                  if (bk === 127) {
                    return ak < 3 ? -1 : 1
                  }
                  return 0
                },
                onChange: (value) => {
                  updateTypeField(value)
                  Segment.track(SegmentEvents.experiments.invoiceEditorSettingsChanged, {
                    invoice_type_changed: value,
                  })
                },
                borderless: true,
              }}
            ></FieldCreator>
            <FieldCreator
              name="template"
              className="subFields"
              model={internal.model}
              schema={internal.schema}
              translator={tsxl}
              props={{
                value: internal.template,
                searchable: true,
                searchPlaceholder: l('search.Default'),
                map: selectMaps.templates,
                automation: automation + '-template',
                onChange: async (value: string) => {
                  await changeFields({
                    ...fields,
                    template: { ...fields.template, value },
                  })
                  changeStyles(settings)
                  Segment.track(SegmentEvents.experiments.invoiceEditorSettingsChanged, {
                    invoice_template_changed: value,
                  })
                },
                borderless: true,
              }}
            ></FieldCreator>
            {[DocumentTypes.CREDITNOTE, DocumentTypes.INVOICE].includes(draft.document) && (
              <ReferenceFieldsSettings
                automation={automation + '-reference-fields-settings'}
                model={internal.model}
                onChangeFields={changeFields}
                fields={fields}
              />
            )}
            <ColumnsSettings
              {...{
                name: 'columns',
                model: internal.model,
                schema: internal.schema,
                value: visibleColumns,
                map: columnsNamesMap,
                automation: automation + '-visible-columns',
                onChange: (value: string[]) => {
                  changeFields({
                    ...fields,
                    columns: { ...fields.columns, value },
                  })

                  const arr1 = visibleColumns.length > value.length ? visibleColumns : value
                  const arr2 = visibleColumns.length > value.length ? value : visibleColumns

                  const difference = arr1.filter((x) => !arr2.includes(x))

                  if (difference && difference.length > 0) {
                    Segment.track(SegmentEvents.experiments.invoiceEditorSettingsChanged, {
                      visible_colums_changed: `${difference[0]} ${visibleColumns.length > value.length ? 'removed' : 'added'}`,
                    })
                  }
                },
                showTotalSelection: columnsNamesMap.taxTotal !== undefined,
              }}
            />
            <FieldCreator
              name="currency"
              className="subFields"
              model={internal.model}
              schema={internal.schema}
              translator={tsxl}
              props={{
                value: fields.currency.value,
                searchable: true,
                searchPlaceholder: l('search.Default'),
                map: selectMaps.currencies,
                automation: automation + '-currency',
                onChange: (selectedCurrency) => {
                  handleCurrencyChange(selectedCurrency)
                  Segment.track(SegmentEvents.experiments.invoiceEditorSettingsChanged, {
                    currency_changed: selectedCurrency,
                  })
                },
                borderless: true,
              }}
            ></FieldCreator>
            {shouldRenderRate && (
              <div className="ExchangeRateContainer">
                <NumberInput
                  decimals={2}
                  key={'exchangeRate'}
                  formatter={getExchangeRateF(props)}
                  automation={automation + '-exchangeRate'}
                  value={getExchangeRate(props)}
                  label={tsxl('ExchangeRate')}
                  onBlur={(value) => {
                    handleExchangeRateChange(value)
                    Segment.track(SegmentEvents.experiments.invoiceEditorSettingsChanged, {
                      exchange_rate_changed: true,
                    })
                  }}
                />
                <div className="ExchangeRateHint">
                  <small>
                    {internal.defaultCurrency}
                    <span className="ExchangeRateHintArrow">&#8594;</span>
                    {fields.currency.value}
                  </small>
                </div>
              </div>
            )}

            <FieldCreator
              name="language"
              className="subFields"
              model={internal.model}
              schema={internal.schema}
              translator={tsxl}
              props={{
                value: internal.language,
                searchable: true,
                searchPlaceholder: l('search.Default'),
                map: selectMaps.languages,
                automation: automation + '-language',
                onChange: (value: string) => {
                  changeFields({ ...fields, locale: { ...fields.locale, value } })
                  Segment.track(SegmentEvents.experiments.invoiceEditorSettingsChanged, {
                    invoice_language_changed: value,
                  })
                },
                borderless: true,
              }}
            ></FieldCreator>

            <FieldCreator
              name="title"
              className="subFields insetBottomShadow invoiceTitleField"
              model={internal.model}
              schema={internal.schema}
              translator={tsxl}
              props={{
                value: fields.title.value,
                automation: automation + '-title',
                onChange: (value: string) => {
                  changeFields({ ...fields, title: { ...fields.title, value } })
                },
                onBlur: () => {
                  Segment.track(SegmentEvents.experiments.invoiceEditorSettingsChanged, { invoice_title_changed: true })
                },
              }}
            >
              <span
                className="AdvancedSettingsLink Label"
                onClick={() => {
                  navigateToInvoicingSettings()
                  Segment.track(SegmentEvents.experiments.invoiceEditorSettingsChanged, {
                    default_settings_changed: true,
                  })
                }}
              >
                {tsxl('InvoiceEditor.doMoreAtAccountSettings')}
              </span>
            </FieldCreator>
          </div>
        </animated.div>
      }
    </>
  )
}

export default InvoiceSettings
